import {
  QeScoreAnalysisResults,
  QeScoreSummary,
  QeScorePriority,
} from '../interfaces';

const normalizeActionType = (actionType: string): string => {
  return actionType
    ? actionType
        .trim()
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : ' ';
};

export const mapQeScoreAnalysisResponse = (
  response: any,
): QeScoreAnalysisResults => {
  const actionItems = (response?.actionItems || []).map(
    (action: any, index: number) => ({
      actionId: action.action_id || `ACTION_${index + 1}`,
      title: action.title || '',
      actionType: normalizeActionType(action.action_type),
      workItemsCount: action.work_items ?? 0,
      priority: normalizePriority(action.priority),
      actionText: action.action || '',
      links: action.links || [],
    }),
  );

  const rawExecutionLogs = Array.isArray(response?.executionLogs)
    ? response.executionLogs
    : Array.isArray(response?.execution_logs)
    ? response.execution_logs
    : [];

  const executionEvents = rawExecutionLogs.map((log: any) => ({
    event: log.event || '',
    timestamp: log.timestamp || '',
    crew: log.crew,
    task: log.task,
    agent: log.agent,
    message: log.message,
  }));

  return {
    reportMetadata: {
      title: response?.reportMetadata?.title || '',
      application: response?.reportMetadata?.application || '',
      apmNumber: response?.reportMetadata?.apm_number || '',
      period: response?.reportMetadata?.period || '',
      generatedOn: response?.reportMetadata?.generated_on || '',
      improvementPotential:
        response?.reportMetadata?.improvement_potential ?? 0,
    },
    totalWorkItems: response?.totalWorkItems ?? 0,
    highPriority: response?.highPriority ?? 0,
    mediumPriority: response?.mediumPriority ?? 0,
    lowPriority: response?.lowPriority ?? 0,
    actionItems,
    executionLogs: {
      apmNumber: rawExecutionLogs?.[0]?.apm_number || '',
      executionDate: rawExecutionLogs?.[0]?.timestamp?.split('T')[0] || '',
      events: executionEvents,
    },
  };
};

export const mapQeScoreSummaryResponse = (response: any): QeScoreSummary => {
  const kpiBreakdown = (response?.kpiBreakdown || []).map((kpi: any) => ({
    process: kpi.process || '',
    weightage: kpi.weightage ?? 0,
    weight: kpi.weight ?? 0,
    status: kpi.status || '',
  }));

  const trendData = (response?.trendData || []).map((trend: any) => ({
    month: trend.month || '',
    maxWeight: trend.maxWeight ?? 0,
  }));

  const sourceCurrentMonthWeight = response?.currentMonthWeight || {};
  const currentMonthWeight = {
    month: sourceCurrentMonthWeight?.month || '',
    maxWeight: sourceCurrentMonthWeight?.maxWeight ?? 0,
  };

  const analysisData = response?.analysisData
    ? mapQeScoreAnalysisResponse(response.analysisData)
    : undefined;

  return {
    requestId: response?.requestId ?? '',
    apmNumber: response?.apmNumber ?? '',
    application: response?.application ?? '',
    kpiBreakdown,
    currentMonthWeight,
    trendData,
    generatedAt: response?.generatedAt ?? '',
    analysisDoneToday: response?.analysisDoneToday ?? 'NOT_STARTED',
    analysisData,
  };
};

const normalizePriority = (priority: string): QeScorePriority => {
  const value = (priority || '').toLowerCase();
  if (value === 'high') return 'High';
  if (value === 'medium') return 'Medium';
  return 'Low';
};
